import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  alphabet,
  email,
  phoneNumber,
  alphaNumericPattern,
} from "../core/regex";
import { postContactForm } from "../../redux/Actions";
import { useSelector } from "react-redux";

const contactUsFormSchema = yup.object({
  name: yup
    .string()
    .required("Name is required")
    .max(50, "Enter cannot exceed 50 character")
    .matches(alphabet, "Name should be only alphabet")
    .trim(),
  email: yup
    .string()
    .required("Email address is required")
    .matches(email, "Enter a valid email address")
    .max(96, "Enter cannot exceed 96 character")
    .trim(),
  mobile: yup
    .string()
    .required("Mobile number is required")
    .matches(phoneNumber, "Enter a valid mobile number")
    .trim(),
  message: yup
    .string()
    .required("Message is required")
    .matches(
      alphaNumericPattern,
      "Message cannot not contain any special character"
    )
    .max(250, "Message cannot exceed 250 character")
    .trim(),
  contact: yup.number().default(1),
});

function ContactAlix() {
  const settingsData: any = useSelector<any>((state) => state?.settings);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error' | null, message: string }>({ type: null, message: '' });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(contactUsFormSchema),
  });

  const onSubmit = async (data: any) => {
    const result = await postContactForm(data);
    if (result?.status) {
      setSubmitStatus({ type: 'success', message: result.message || 'Your message has been sent successfully!' });
      reset();
    } else {
      setSubmitStatus({ type: 'error', message: result?.message || 'Failed to send message. Please try again.' });
    }
  };

  return (
    <>
      <div className="breadcrumpset">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumpview">
                <h2>Contact Us</h2>
                <ul>
                  <li>
                    <a href="/">
                      <i className="fa fa-home" aria-hidden="true"></i>{" "}
                    </a>
                  </li>
                  <li>
                    <span>Contact Us</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card shadow-sm my-4">
              <div className="card-body p-4">
                <h3 className="card-title text-primary mb-4">Get In Touch</h3>
                {submitStatus.type && (
                  <div className={`alert ${submitStatus.type === 'success' ? 'alert-success' : 'alert-danger'} alert-dismissible fade show mb-3`} role="alert">
                    {submitStatus.message}
                    <button type="button" className="btn-close" onClick={() => setSubmitStatus({ type: null, message: '' })}></button>
                  </div>
                )}
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      {...register("name")}
                      placeholder="Enter your full name"
                    />
                    {errors.name && (
                      <small className="text-danger">{errors.name.message}</small>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      {...register("email")}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <small className="text-danger">{errors.email.message}</small>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Phone Number</label>
                    <input
                      type="phone"
                      className="form-control"
                      {...register("mobile")}
                      placeholder="Enter your phone number"
                    />
                    {errors.mobile && (
                      <small className="text-danger">{errors.mobile.message}</small>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Subject</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter subject"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Your Message</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      {...register("message")}
                      placeholder="Enter your message"
                    ></textarea>
                    {errors.message && (
                      <small className="text-danger">{errors.message.message}</small>
                    )}
                  </div>
                  <button type="submit" className="btn btn-primary w-100 mt-2">
                    Contact Us
                  </button>
                </form>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card bg-primary text-white shadow-sm my-4">
              <div className="card-body p-4">
                <h3 className="card-title text-white mb-4">Contact Information</h3>
                <div className="d-flex align-items-start mb-4">
                  <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '50px', height: '50px', flexShrink: 0}}>
                    <i className="fa fa-map-marker"></i>
                  </div>
                  <div>
                    <h5 className="mb-1">Our Location</h5>
                    <p className="mb-0 opacity-75">{settingsData?.WebsiteSettings?.contact_t1_en}</p>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-4">
                  <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '50px', height: '50px', flexShrink: 0}}>
                    <i className="fa fa-phone"></i>
                  </div>
                  <div>
                    <h5 className="mb-1">Phone</h5>
                    <a href={`tel:${settingsData?.WebsiteSettings?.contact_t3}`} target="_blank" className="text-white opacity-75 text-decoration-none">
                      {settingsData?.WebsiteSettings?.contact_t3}
                    </a>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-4">
                  <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '50px', height: '50px', flexShrink: 0}}>
                    <i className="fa fa-envelope"></i>
                  </div>
                  <div>
                    <h5 className="mb-1">Email Address</h5>
                    <a href={`mailto:${settingsData?.WebsiteSettings?.contact_t6}`} target="_blank" className="text-white opacity-75 text-decoration-none">
                      {settingsData?.WebsiteSettings?.contact_t6}
                    </a>
                  </div>
                </div>
                <div className="d-flex align-items-start">
                  <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '50px', height: '50px', flexShrink: 0}}>
                    <i className="fa fa-share-alt"></i>
                  </div>
                  <div>
                    <h5 className="mb-2">Follow Us On</h5>
                    <div className="d-flex gap-2">
                      <a href={settingsData?.WebsiteSettings?.facebook_link} target="_blank" className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center text-white text-decoration-none" style={{width: '40px', height: '40px', cursor: 'pointer', transition: 'all 0.3s ease'}} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = 'var(--primary-color)'; e.currentTarget.style.transform = 'translateY(-3px)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        <i className="fab fa-facebook-f"></i>
                      </a>
                      <a href={settingsData?.WebsiteSettings?.twitter_link} target="_blank" className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center text-white text-decoration-none" style={{width: '40px', height: '40px', cursor: 'pointer', transition: 'all 0.3s ease'}} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = 'var(--primary-color)'; e.currentTarget.style.transform = 'translateY(-3px)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        <i className="fab fa-twitter"></i>
                      </a>
                      <a href={settingsData?.WebsiteSettings?.instagram_link} target="_blank" className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center text-white text-decoration-none" style={{width: '40px', height: '40px', cursor: 'pointer', transition: 'all 0.3s ease'}} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = 'var(--primary-color)'; e.currentTarget.style.transform = 'translateY(-3px)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        <i className="fab fa-instagram"></i>
                      </a>
                      <a href={settingsData?.WebsiteSettings?.linkedin_link} target="_blank" className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center text-white text-decoration-none" style={{width: '40px', height: '40px', cursor: 'pointer', transition: 'all 0.3s ease'}} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = 'var(--primary-color)'; e.currentTarget.style.transform = 'translateY(-3px)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        <i className="fab fa-linkedin-in"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default ContactAlix;
